import {
  GoalOverviewRoadmapViewOptions,
  TimelineTimeframes,
  Timestamp,
} from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  CircleSmall,
  FilterMinimalStyleCircle16,
  SettingsSort18,
  Table14,
  Timeline214,
} from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Variants as IconButtonVariants,
} from 'shared/components/ui/icon-button';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useToggle } from 'shared/hooks/use-toggle';
import { useTheme } from 'styled-components';

import { ActiveMenu } from './active-menu';
import { FilterBar } from './filter-bar';
import { HeaderButton } from './header-button';
import * as Styled from './page-top-bar.style';

export type PageTopBarProps = {
  selectedView: GoalOverviewRoadmapViewOptions;
  onChangeView: (view: GoalOverviewRoadmapViewOptions) => void;

  timeframe: TimelineTimeframes;
  onMonth: () => void;
  onQuarter: () => void;
  onYear: () => void;
  onFiveYears: () => void;

  referenceDate: Timestamp;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  selectedView,
  onChangeView,

  timeframe,
  onMonth,
  onQuarter,
  onYear,
  onFiveYears,

  referenceDate,
  onPrev,
  onToday,
  onNext,
}) => {
  const theme = useTheme();
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, toggleFiltersOpen] = useToggle(false);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const viewOptions = useMemo<Option<GoalOverviewRoadmapViewOptions>[][]>(
    () => [
      [
        {
          icon: Table14,
          value: GoalOverviewRoadmapViewOptions.List,
        },
        {
          icon: Timeline214,
          value: GoalOverviewRoadmapViewOptions.Timeline,
        },
      ],
    ],
    [],
  );

  const timeframeOptions = useMemo<Option<TimelineTimeframes>[][]>(
    () => [
      [
        {
          label: t('general.labels.timeframes.month.short'),
          value: TimelineTimeframes.Month,
        },
        {
          label: t('general.labels.timeframes.quarter.short'),
          value: TimelineTimeframes.Quarter,
        },
        {
          label: t('general.labels.timeframes.year.short'),
          value: TimelineTimeframes.Year,
        },
        {
          label: t('general.labels.timeframes.five-years.short'),
          value: TimelineTimeframes.FiveYears,
        },
      ],
    ],
    [t],
  );

  const _onTimelineOption = (value: TimelineTimeframes) => {
    switch (value) {
      case TimelineTimeframes.Month:
        return onMonth();
      case TimelineTimeframes.Quarter:
        return onQuarter();
      case TimelineTimeframes.Year:
        return onYear();
      case TimelineTimeframes.FiveYears:
        return onFiveYears();
    }
  };

  return (
    <>
      <TopBarBase>
        <Styled.Container>
          <Styled.Title>
            <Typography variant="h5" component="h1">
              {t('pages.goals.title')}
            </Typography>
          </Styled.Title>

          <Styled.CenterInteractions>
            <SliderButtons
              optionGroups={viewOptions}
              onOption={onChangeView}
              value={selectedView}
              preventDrag
            />
            {selectedView === GoalOverviewRoadmapViewOptions.Timeline && (
              <>
                <SliderButtons
                  optionGroups={timeframeOptions}
                  onOption={_onTimelineOption}
                  value={timeframe}
                  preventDrag
                />

                <Styled.TimeSelectionContainer>
                  <Styled.TimeSelectionButton onClick={onPrev}>
                    <Icon icon={ChevronLeft} />
                  </Styled.TimeSelectionButton>
                  <Styled.TimeSelectionButton onClick={onToday}>
                    <Icon icon={CircleSmall} />
                  </Styled.TimeSelectionButton>
                  <Styled.TimeSelectionButton onClick={onNext} $rotated>
                    <Icon icon={ChevronLeft} />
                  </Styled.TimeSelectionButton>
                </Styled.TimeSelectionContainer>
              </>
            )}
          </Styled.CenterInteractions>

          <Styled.Interactions>
            {selectedView !== GoalOverviewRoadmapViewOptions.List ? (
              <Button
                variant={Variants.Text}
                size={Sizes.Small}
                color={Colors.Inherit}
                endIcon={FilterMinimalStyleCircle16}
                onClick={toggleFiltersOpen}
                preventDrag
              >
                {t('general.labels.filter')}
              </Button>
            ) : (
              <>
                <IconButton
                  ref={filterButtonRef}
                  icon={SettingsSort18}
                  size={Sizes.Medium}
                  color={IconButtonColors.Default}
                  onClick={openMenu}
                  variant={
                    theme.isMobile
                      ? IconButtonVariants.Outlined
                      : IconButtonVariants.Contained
                  }
                  preventDrag
                />

                {menuOpen && (
                  <ActiveMenu
                    referenceElement={filterButtonRef}
                    onClose={closeMenu}
                  />
                )}
              </>
            )}

            <HeaderButton />
          </Styled.Interactions>
        </Styled.Container>
      </TopBarBase>

      {selectedView !== GoalOverviewRoadmapViewOptions.List && (
        <Styled.FilterContainer $open={filtersOpen}>
          <FilterBar />
        </Styled.FilterContainer>
      )}
    </>
  );
};
